// Access Point detail page. Reached via /access-points/:id.

import React, {useEffect, useState} from 'react'
import clsx from 'clsx'
import {useNavigate, useParams} from 'react-router-dom'
import {Wifi} from 'lucide-react'
import {Header} from '../../components/Header'
import {PageShell} from '../../components/PageShell'
import {supabase} from '../../app/supabaseClient'
import type {APDetail, APStatus} from './types'

const STATUS_PILL_CLASS: Record<APStatus, string> = {
  active:  'bg-badgeGreenBg text-statusGreen',
  offline: 'bg-surfaceLight text-textMuted',
  fault:   'bg-badgeRedBg text-statusRed',
}

export const APDetailScreen: React.FC = () => {
  const {id} = useParams<{id: string}>()
  const navigate = useNavigate()
  const [ap, setAP] = useState<APDetail | null>(null)
  const [parentName, setParentName] = useState<string | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    if (!id) return
    let cancelled = false
    ;(async () => {
      setLoading(true)
      const {data, error: e} = await supabase
        .from('access_points')
        .select(
          'id, location_id, name, mac_address, ssid, ip_address, is_active, status, ' +
            'x, y, z, level, radius_m',
        )
        .eq('id', id)
        .single<APDetail>()
      if (cancelled) return
      if (e || !data) {
        setError(e?.message ?? 'Not found')
        setLoading(false)
        return
      }
      setAP(data)
      if (data.location_id) {
        const {data: loc} = await supabase
          .from('locations')
          .select('name')
          .eq('id', data.location_id)
          .single<{name: string}>()
        if (!cancelled) setParentName(loc?.name ?? null)
      }
      setLoading(false)
    })()
    return () => {
      cancelled = true
    }
  }, [id])

  return (
    <main className="flex flex-1 flex-col overflow-hidden">
      <Header title={ap?.name ?? 'Access Point'} backTo="/access-points" />
      <PageShell
        footer={
          ap && (
            <button
              type="button"
              onClick={() => navigate(`/access-points/${ap.id}/edit`)}
              className="rounded-md border border-primary px-lg py-sm text-md font-semibold text-primary hover:bg-primaryBg"
            >
              Edit
            </button>
          )
        }
      >
        {loading ? (
          <div className="flex h-full items-center justify-center text-textMuted">Loading…</div>
        ) : error || !ap ? (
          <div className="rounded-lg border border-statusRed bg-surfaceCard p-xxl">
            <h3 className="mb-md text-lg font-semibold text-statusRed">Fetch failed</h3>
            <pre className="whitespace-pre-wrap font-mono text-sm text-textPrimary">{error ?? 'Not found'}</pre>
          </div>
        ) : (
          <div className="flex flex-col gap-xl">
            <div className="flex items-center gap-lg">
              <div className="flex h-14 w-14 items-center justify-center rounded-md bg-surfaceLight">
                <Wifi size={28} color="var(--color-primary)" />
              </div>
              <div>
                <div className="text-xxl font-bold text-textPrimary">{ap.name}</div>
                <div className="text-md text-textMuted">{ap.ssid}</div>
              </div>
              <div className="ml-auto">
                <span
                  className={clsx(
                    'rounded-round px-md py-xs text-xs font-semibold uppercase',
                    STATUS_PILL_CLASS[ap.status],
                  )}
                >
                  {ap.status}
                </span>
              </div>
            </div>

            <dl className="grid grid-cols-2 gap-x-xl gap-y-md rounded-md border border-surfaceBorder bg-surfaceCard p-lg">
              <ViewField label="Parent Location" value={parentName ?? '—'} />
              <ViewField label="Active" value={ap.is_active ? 'Yes' : 'No'} />
              <ViewField label="MAC address" value={ap.mac_address ?? '—'} mono />
              <ViewField label="IP address" value={ap.ip_address ?? '—'} mono />
              <ViewField label="SSID" value={ap.ssid ?? '—'} />
              <ViewField label="Coverage radius (m)" value={ap.radius_m != null ? String(ap.radius_m) : '—'} />
              <ViewField label="X" value={ap.x != null ? ap.x.toFixed(2) : '—'} />
              <ViewField label="Y" value={ap.y != null ? ap.y.toFixed(2) : '—'} />
              <ViewField label="Z" value={ap.z != null ? ap.z.toFixed(2) : '—'} />
              <ViewField label="Level" value={ap.level != null ? String(ap.level) : '—'} />
            </dl>
          </div>
        )}
      </PageShell>
    </main>
  )
}

const ViewField: React.FC<{label: string; value: string; mono?: boolean}> = ({label, value, mono}) => (
  <div>
    <dt className="text-xs uppercase tracking-wider text-textMuted">{label}</dt>
    <dd className={clsx('mt-1 text-md text-textPrimary', mono && 'font-mono')}>{value}</dd>
  </div>
)
